import * as Sentry from "@sentry/react";

import { AxiosError } from "axios";

let sentryInitialized = false;

const isNetworkError = (error: Sentry.Event): boolean => {
  const exception = error.exception?.values?.[0];
  const value = exception?.value || "";

  // Ignore Apple "Load Failed" and "Failed to fetch" errors - they could be any network error
  // https://sentry.io/answers/load-failed-javascript/
  // https://sentry.io/answers/failed-to-fetch-javascript/
  const ignoredTypeErrors = ["Load failed", "Failed to fetch"];
  if (exception?.type === "TypeError" && ignoredTypeErrors.includes(value)) {
    return true;
  }
  return false;
};

// Facebook and Instagram in-app browsers identify themselves via FBAN/FBAV or Instagram in the user agent
const isFacebookOrInstagramBrowser = (): boolean => {
  const userAgent = window.navigator?.userAgent || "";
  return /FBAN|FBAV|Instagram/i.test(userAgent);
};

// Facebook and Instagram in-app browsers throw InvalidAccessError from their injected scripts;
// these are outside our control and not actionable.
const isInAppBrowserInvalidAccessError = (error: Sentry.Event): boolean => {
  const exception = error.exception?.values?.[0];
  return (
    exception?.type === "InvalidAccessError" && isFacebookOrInstagramBrowser()
  );
};

const transformAxiosEvent = (event: Sentry.Event, hint: Sentry.EventHint) => {
  const err = hint.originalException as AxiosError;
  if (!err?.isAxiosError) {
    return event;
  }
  return {
    ...event,
    fingerprint: [err.name, err.message, err.config?.urlWithPathParams || ""],
    transaction: err.config?.urlWithPathParams,
    extra: {
      ...event.extra,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      errorMessage: (err.response?.data as any)?.errorMessage,
    },
  };
};

const beforeSend = (
  event: Sentry.Event,
  hint: Sentry.EventHint,
): Sentry.Event | null => {
  if (window.ENV.NOOM_ENVIRONMENT === "development") {
    // eslint-disable-next-line no-console
    console.log("Sentry Event (not sent)", event);
    return null;
  }
  if (window.ENV.NOOM_ENVIRONMENT === "test") {
    return null;
  }
  if (isNetworkError(event)) {
    return null;
  }
  if (isInAppBrowserInvalidAccessError(event)) {
    return null;
  }

  return transformAxiosEvent(event, hint);
};

export function initializeSentry() {
  if (sentryInitialized) {
    return;
  }
  sentryInitialized = true;

  Sentry.init({
    beforeSend,
    release: process.env.HIVE_SHA || "dev",
    dsn: "https://60703e580c164301b457b00b9df7c1ae@o1000882.ingest.sentry.io/6528389",
    environment: window.ENV.NOOM_ENVIRONMENT,
    ignoreErrors: [
      "__gCrWeb.learningToolsRuntimeBridge.raiseMessageFromHost",
      "AbortError: The play() request was interrupted because video-only background media was paused to save power.",
      "ceCurrentVideo.currentTime",
      "ChunkLoadError",
      "R.tracking_details",
      "Network Error",
      "Failed to load script",
      "Request aborted",
      "updateAndCleanExtractedContent",
      "REquest is not defined",
      // These two are errors experienced by Microsoft crawlers as part of Outlook Safe Links checking.
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      "Tab not found",
      "Java object is gone",
    ],
    denyUrls: [
      "cookielaw.org",
      "googleapis.com", // Errors in minified Google Translate scripts
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
  });
}

export function captureException(
  err: Error | unknown,
  extra?: Record<string, unknown>,
) {
  initializeSentry();
  Sentry.captureException(err, { extra });
}

// https://docs.sentry.io/platforms/javascript/enriching-events/level/
type SentrySeverityLevel =
  | "fatal"
  | "error"
  | "warning"
  | "log"
  | "info"
  | "debug";
export function captureMessage(message: string, level?: SentrySeverityLevel) {
  initializeSentry();
  if (level) {
    Sentry.captureMessage(message, level);
  } else {
    // don't pass level so that the default level will be used
    Sentry.captureMessage(message);
  }
}
